import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IExplodingFeeScheduleRuleset,
  IExplodingFeeScheduleCreateRequest,
  IExplodingFeeScheduleUpdateRequest,
} from "types/ExplodingFeeSchedule/ExplodingFeeSchedule";
import {
  IAllExplodingFeeScheduleResponse,
  IExplodingFeeScheduleResponse,
} from "types/ExplodingFeeSchedule/ExplodingFeeScheduleResponse";

import { api } from ".";

const explodingFeeScheduleQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllExplodingFeeSchedules: builder.query<
      IAllExplodingFeeScheduleResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `exploding-fee-schedules/all${search}`;
      },
      providesTags: ["ExplodingFeeScheduleRulesets"],
    }),
    getExplodingFeeScheduleById: builder.query<
      IExplodingFeeScheduleResponse,
      string | undefined
    >({
      query: (id: string) => `exploding-fee-schedules/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "ExplodingFeeScheduleRulesets", id: id ?? "LIST" },
      ],
    }),
    createExplodingFeeSchedule: builder.mutation<
      IExplodingFeeScheduleRuleset,
      IExplodingFeeScheduleCreateRequest
    >({
      query: (newRuleset) => ({
        url: "exploding-fee-schedules",
        method: "POST",
        body: newRuleset,
      }),
      invalidatesTags: ["ExplodingFeeScheduleRulesets", "ContractFeeSchedules"],
    }),
    updateExplodingFeeSchedule: builder.mutation<
      IExplodingFeeScheduleRuleset,
      IExplodingFeeScheduleUpdateRequest
    >({
      query: (updatedRuleset) => ({
        url: `exploding-fee-schedules/${updatedRuleset.id}`,
        method: "PUT",
        body: updatedRuleset,
      }),
      invalidatesTags: [
        "ExplodingFeeScheduleRulesets",
        "ContractFeeSchedules",
        "DefaultRulePropagation",
      ],
    }),
    deleteExplodingFeeSchedule: builder.mutation<any, string>({
      query: (id) => ({
        url: `exploding-fee-schedules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ExplodingFeeScheduleRulesets",
        "ContractFeeSchedules",
        "DefaultRulePropagation",
      ],
    }),
    uploadExplodingFeeScheduleCsv: builder.mutation<
      { data: { created: number; errors: string[] } },
      FormData
    >({
      query: (formData) => ({
        url: "exploding-fee-schedules/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["ExplodingFeeScheduleRulesets", "ContractFeeSchedules"],
    }),
    cloneExplodingFeeScheduleAsDefault: builder.mutation<
      IExplodingFeeScheduleResponse,
      string
    >({
      query: (id) => ({
        url: `exploding-fee-schedules/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["ExplodingFeeScheduleRulesets"],
    }),
    copyExplodingFeeScheduleToContract: builder.mutation<
      IExplodingFeeScheduleResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `exploding-fee-schedules/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ExplodingFeeScheduleRulesets", "ContractFeeSchedules"],
    }),
    getExplodingFeeSchedulePropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `exploding-fee-schedules/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `exploding:${id}` },
      ],
    }),
    applyExplodingFeeSchedulePropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `exploding-fee-schedules/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "ExplodingFeeScheduleRulesets",
        "ContractFeeSchedules",
        { type: "DefaultRulePropagation", id: `exploding:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllExplodingFeeSchedulesQuery,
  useGetExplodingFeeScheduleByIdQuery,
  useCreateExplodingFeeScheduleMutation,
  useUpdateExplodingFeeScheduleMutation,
  useDeleteExplodingFeeScheduleMutation,
  useUploadExplodingFeeScheduleCsvMutation,
  useCloneExplodingFeeScheduleAsDefaultMutation,
  useCopyExplodingFeeScheduleToContractMutation,
  useGetExplodingFeeSchedulePropagationPreviewQuery,
  useLazyGetExplodingFeeSchedulePropagationPreviewQuery,
  useApplyExplodingFeeSchedulePropagationMutation,
} = explodingFeeScheduleQueries;
